import React from "react";
import { Box, Typography, Collapse, Fade } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { ChevronDown, ChevronUp } from "lucide-react";

export const useStyles = makeStyles((theme) => ({
  expandBadge: {
    display: "flex",
    alignItems: "center",
    width: "fit-content",
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.spacing(1.5),
    cursor: "pointer",
    margin: theme.spacing(1, 0, 1, 2),
    userSelect: "none",
    gap: theme.spacing(0.5),
    transition: "opacity 0.2s",
    "&:hover": { opacity: 0.8 },
  },
  badgeText: {
    fontSize: "12px",
    fontWeight: 500,
  },
  badgeDivider: {
    flexGrow: 1,
    height: "1px",
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.divider,
  },
  badgeStyle: {
    backgroundColor: theme.palette.border.tertiary,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
  },
  explanationText: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(2.5),
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    maxWidth: 520,
    transform: "translateY(5px)",
  },
}));

interface CollapsibleSetSectionProps {
  title: string;
  showCollapse: boolean;
  allSetsCompleted: boolean;
  children: (isOpen: boolean) => React.ReactNode;
  allSetsCompletedText?: string;
  disableLeftMargin?: boolean;
}

export const CollapsibleSetSection: React.FC<CollapsibleSetSectionProps> = ({
  title,
  showCollapse,
  allSetsCompleted,
  children,
  allSetsCompletedText = "All sets completed. Compare to original workout above.",
  disableLeftMargin = false,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const s = useStyles();

  return (
    <Box sx={{ width: "100%" }}>
      {children(isOpen)}

      <Collapse in={allSetsCompleted && isOpen} timeout={400} unmountOnExit>
        <Fade in={allSetsCompleted && isOpen} timeout={400}>
          <Box>
            <Typography
              className={s.explanationText}
              sx={
                disableLeftMargin ? { marginLeft: "0 !important" } : undefined
              }
            >
              {allSetsCompletedText}
            </Typography>
          </Box>
        </Fade>
      </Collapse>

      <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
        <Box
          className={clsx(s.expandBadge, s.badgeStyle)}
          sx={disableLeftMargin ? { marginLeft: "0 !important" } : undefined}
          onClick={() => setIsOpen(!isOpen)}
        >
          <Typography className={s.badgeText}>{title}</Typography>
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </Box>
        <div className={s.badgeDivider} />
      </Box>
    </Box>
  );
};

export default CollapsibleSetSection;
